import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { FaShareAlt, FaGift } from "react-icons/fa";

const API_BASE = "https://qr.billsarthi.in/public/api";

const ReferralPage = () => {
  const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    referral_code: "",
    share_message: "",
    stats: {},
    rewards: [],
    pending_friends: [],
  });

  const loadInfo = useCallback(async () => {
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/referral/info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: user.id }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json?.success) {
        throw new Error(json?.message || "Failed to load referral info");
      }
      setData({
        referral_code: json?.referral_code || "",
        share_message: json?.share_message || "",
        stats: json?.stats || {},
        rewards: Array.isArray(json?.rewards) ? json.rewards : [],
        pending_friends: Array.isArray(json?.pending_friends) ? json.pending_friends : [],
      });
    } catch (error) {
      toast.error(error?.message || "Failed to load referral info");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  const shareNow = async () => {
    const text = data?.share_message || "Join Bill Sarthi";
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Referral message copied");
      }
    } catch {
      toast.info("Sharing cancelled");
    }
  };

  const redeem = async (reward, rewardType) => {
    try {
      const payload = {
        restaurantid: user.id,
        reward_id: reward.id,
        reward_type: rewardType,
      };
      if (rewardType === "upi") {
        const upi = window.prompt("Enter UPI ID");
        if (!upi) return;
        payload.upi_vpa = upi.trim();
      }

      const response = await fetch(`${API_BASE}/referral/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json?.success) {
        throw new Error(json?.message || "Failed to redeem reward");
      }
      toast.success(json?.message || "Reward redeemed");
      loadInfo();
    } catch (error) {
      toast.error(error?.message || "Failed to redeem reward");
    }
  };

  return (
    <Page>
      <Hero>
        <div>
          <h1>Referral</h1>
          <p>Invite restaurants and unlock rewards.</p>
        </div>
        <Code>{data.referral_code || "-"}</Code>
      </Hero>

      <Stats>
        <Stat>
          <strong>{data?.stats?.total_referred ?? 0}</strong>
          <span>Total referred</span>
        </Stat>
        <Stat>
          <strong>{data?.stats?.qualified_referred ?? 0}</strong>
          <span>Qualified</span>
        </Stat>
        <Stat>
          <strong>{data?.stats?.pending_rewards ?? 0}</strong>
          <span>Pending rewards</span>
        </Stat>
      </Stats>

      <Actions>
        <Btn type="button" $primary onClick={shareNow} disabled={loading || !data.share_message}>
          <FaShareAlt /> Share Invite
        </Btn>
      </Actions>

      <Card>
        <h3>Eligible Rewards</h3>
        {loading ? (
          <Empty>Loading...</Empty>
        ) : data.rewards.length === 0 ? (
          <Empty>No rewards available yet.</Empty>
        ) : (
          data.rewards.map((reward) => (
            <Reward key={reward.id}>
              <div>
                <strong>{reward?.referee_name || "Friend"}</strong>
                <small>Status: {reward?.reward_status || "unknown"}</small>
              </div>
              {reward?.reward_status === "eligible" && !reward?.reward_type ? (
                <ActionRow>
                  <SmallBtn type="button" onClick={() => redeem(reward, "upi")}>
                    <FaGift /> Redeem UPI
                  </SmallBtn>
                  <SmallBtn type="button" onClick={() => redeem(reward, "starter_3m")}>
                    <FaGift /> Redeem Plan
                  </SmallBtn>
                </ActionRow>
              ) : (
                <small>{reward?.reward_type || "No action"}</small>
              )}
            </Reward>
          ))
        )}
      </Card>

      <Card>
        <h3>Pending Friends</h3>
        {loading ? (
          <Empty>Loading...</Empty>
        ) : data.pending_friends.length === 0 ? (
          <Empty>No pending friends.</Empty>
        ) : (
          data.pending_friends.map((friend) => (
            <Friend key={friend.id || friend.referee_name}>
              <span>{friend?.referee_name || "-"}</span>
              <small>{friend?.status || "pending"}</small>
            </Friend>
          ))
        )}
      </Card>
    </Page>
  );
};

export default ReferralPage;

const Page = styled.div`
  min-height: 100vh;
  padding: 16px;
  background: #f3f5fb;
  display: grid;
  gap: 12px;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, #111827, #0f172a);
  color: #fff;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  h1 { margin: 0; font-size: 1.4rem; }
  p { margin: 6px 0 0; opacity: 0.9; }
`;

const Code = styled.div`
  border: 1px dashed #facc15;
  color: #facc15;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 800;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
`;

const Stat = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  strong { display: block; font-size: 1.2rem; }
  span { color: #64748b; font-size: 12px; }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Btn = styled.button`
  height: 40px;
  border-radius: 9px;
  border: 1px solid ${({ $primary }) => ($primary ? "#015b9a" : "#cbd5e1")};
  background: ${({ $primary }) => ($primary ? "#015b9a" : "#fff")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#0f172a")};
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-weight: 700;
  cursor: pointer;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  h3 { margin: 0 0 10px; }
`;

const Reward = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  strong { display: block; color: #0f172a; }
  small { color: #64748b; }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 6px;
`;

const SmallBtn = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  height: 32px;
  padding: 0 10px;
  display: inline-flex;
  gap: 6px;
  align-items: center;
  font-weight: 700;
  cursor: pointer;
`;

const Friend = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
`;

const Empty = styled.div`
  color: #64748b;
`;
